import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FieldDefinitionModel, DocumentSessionModel, DocumentProcessTypeEnum, ValidationResultModel, BillType } from 'api';

export type FieldMapping = {
  targetField: string;
  sourceField: string;
}


interface ImportState {
  currentStep: number;
  importType: DocumentProcessTypeEnum | null;
  selectedFile: File | null;
  fileName: string | null;
  selectedTemplateId: number | null;
  sessionId: number | null;
  sourceFields: string[];
  targetFields: FieldDefinitionModel[];
  fieldMappings: FieldMapping[];
  validationResult: ValidationResultModel | null;
  importResult: {
    totalRows: number;
    createdCount: number;
    updatedCount: number;
    skippedCount: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  selectedClientOrg: number | null;
  selectedWarehouse: number | null;
  selectedBillType: BillType | null;
  templateName: string | null;
  templateDescription: string | null;
  saveAsTemplate: boolean;
  templateId: number | null;
}

const initialState: ImportState = {
  currentStep: 0,
  importType: null,
  selectedFile: null,
  fileName: null,
  selectedTemplateId: null,
  sessionId: null,
  sourceFields: [],
  targetFields: [],
  fieldMappings: [],
  validationResult: null,
  importResult: null,
  isLoading: false,
  error: null,
  selectedClientOrg: null,
  selectedWarehouse: null,
  selectedBillType: null,
  templateName: null,
  templateDescription: null,
  saveAsTemplate: false,
  templateId: null,
};

export const importSlice = createSlice({
  name: 'import',
  initialState,
  reducers: {
    setSaveAsTemplate: (state, action: PayloadAction<boolean>) => {
      state.saveAsTemplate = action.payload;
    },
    setTemplateName: (state, action: PayloadAction<string>) => {
      state.templateName = action.payload;
    },
    setTemplateDescription: (state, action: PayloadAction<string>) => {
      state.templateDescription = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setImportType: (state, action: PayloadAction<DocumentProcessTypeEnum>) => {
      state.importType = action.payload;
    },
    setSelectedFile: (state, action: PayloadAction<File>) => {
      state.selectedFile = action.payload;

      if (action.payload) {
        state.fileName = action.payload.name;
      }
    },
    setSelectedTemplateId: (state, action: PayloadAction<number | null>) => {
      state.selectedTemplateId = action.payload;
    },
    setSessionId: (state, action: PayloadAction<number>) => {
      state.sessionId = action.payload;
    },
    setSessionData: (state, action: PayloadAction<DocumentSessionModel>) => {
      state.sessionId = action.payload.id!;
      state.fileName = action.payload.fileName!;
      state.importType = action.payload.processType!;
      
      // Parse fieldMappingConfig if it exists
      if (action.payload.fieldMappingConfig) {
        try {
          const mapping = JSON.parse(action.payload.fieldMappingConfig);
          state.fieldMappings = Object.entries(mapping).map(([targetField, sourceField]) => ({
            targetField,
            sourceField: sourceField as string,
          }));
        } catch (e) {
          console.error('Error parsing field mapping config', e);
        }
      }
    },
    setSourceFields: (state, action: PayloadAction<string[]>) => {
      state.sourceFields = action.payload;
    },
    setTargetFields: (state, action: PayloadAction<FieldDefinitionModel[]>) => {
      state.targetFields = action.payload;
    },
    addFieldMapping: (state, action: PayloadAction<FieldMapping>) => {
      // Remove any existing mapping with same target or source field
      state.fieldMappings = state.fieldMappings.filter(
        (mapping) => 
          mapping.targetField !== action.payload.targetField && 
          mapping.sourceField !== action.payload.sourceField
      );
      
      // Add new mapping
      state.fieldMappings.push(action.payload);
    },
    removeFieldMapping: (state, action: PayloadAction<string>) => {
      state.fieldMappings = state.fieldMappings.filter(
        (mapping) => mapping.targetField !== action.payload
      );
    },
    setValidationResult: (state, action: PayloadAction<ValidationResultModel>) => {
      state.validationResult = action.payload;
    },
    setImportResult: (state, action: PayloadAction<{
      totalRows: number;
      createdCount: number;
      updatedCount: number;
      skippedCount: number;
    }>) => {
      state.importResult = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetImport: (state) => {
      return initialState;
    },
    setSelectedClientOrg: (state, action: PayloadAction<number>) => {
      state.selectedClientOrg = action.payload;
    },
    setSelectedWarehouse: (state, action: PayloadAction<number>) => {
      state.selectedWarehouse = action.payload;
    },
    setSelectedBillType: (state, action: PayloadAction<BillType | null>) => {
      state.selectedBillType = action.payload;
    },
    setTemplateId: (state, action: PayloadAction<number | null>) => {
      state.templateId = action.payload;
    },
  },
});

export const {
  setCurrentStep,
  setImportType,
  setSelectedFile,
  setSelectedTemplateId,
  setSessionId,
  setSessionData,
  setSourceFields,
  setTargetFields,
  addFieldMapping,
  removeFieldMapping,
  setValidationResult,
  setImportResult,
  setLoading,
  setError,
  resetImport,
  setSelectedClientOrg,
  setSelectedWarehouse,
  setSelectedBillType,
} = importSlice.actions;

export default importSlice.reducer; 